import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adsApi } from '@/api/client';
import { parseReportRowsPayload, type ReportRow } from '@/api/keywordMetrics';
import {
  keywordReportAdProductsQueryParam,
  reportAdProductsQueryParam,
  targetReportAdProductsQueryParam,
} from '@/api/reportAdProducts';
import type { EntityGraph } from '@/utils/entityCrossFilter';
import {
  adProductCampaignKeysParam,
  adProductMapsFromSummary,
  buildKeywordRowLookup,
  buildTargetRowLookup,
  buildWorkspaceMetricsMaps,
  emptyAdProductMap,
  filterKeywordRowsInScope,
  metricsForKeywordGroup,
  type AdProductSummaryPayload,
  type WorkspaceMetricsMaps,
} from '@/utils/workspaceAdProductMetrics';
import {
  campaignKeysQueryParam,
  targetingTypeMapsFromSummary,
  type TargetingTypeDerivedMetrics,
  type TargetingTypeId,
  type TargetingTypeSummaryPayload,
} from '@/utils/workspaceTargetingTypeMetrics';
import {
  matchTypeMapsFromSummary,
  type MatchTypeDerivedMetrics,
  type MatchTypeId,
  type MatchTypeSummaryPayload,
} from '@/utils/workspaceMatchTypeMetrics';
import {
  aggregatePlacementMetricsByCampaign,
  placementMapsFromSummary,
  type PlacementDerivedMetrics,
  type PlacementId,
  type PlacementReportRow,
  type PlacementSummaryPayload,
} from '@/utils/workspacePlacementMetrics';
import {
  emptyAudienceMetricsMap,
  demoAudienceMetricsMap,
  DEMO_PROFILE_ID,
  type AudienceDerivedMetrics,
  type AudienceSegmentId,
} from '@/utils/workspaceAudienceMetrics';
import type { SnapshotRangeState } from '@/components/snapshot/SnapshotDateRangePicker';
import { snapshotRangesValid } from '@/components/snapshot/SnapshotDateRangePicker';

const EMPTY_REPORT_ROWS: ReportRow[] = [];

/** Cap report row fetches to avoid loading unbounded data into the browser. */
export const WORKSPACE_REPORT_ROW_LIMIT = 2000;

const EMPTY_MAPS: WorkspaceMetricsMaps = {
  byAdProduct: new Map(),
  byCampaignKey: new Map(),
  byPortfolioId: new Map(),
  byAdGroupId: new Map(),
};

const EMPTY_TARGETING_TYPE_MAPS = targetingTypeMapsFromSummary(null);
const EMPTY_TARGETING_TYPE_MAP = EMPTY_TARGETING_TYPE_MAPS.current;
const EMPTY_MATCH_TYPE_MAPS = matchTypeMapsFromSummary(null);
const EMPTY_MATCH_TYPE_MAP = EMPTY_MATCH_TYPE_MAPS.current;
const EMPTY_PLACEMENT_MAPS = placementMapsFromSummary(null);
const EMPTY_PLACEMENT_MAP = EMPTY_PLACEMENT_MAPS.current;
const EMPTY_PLACEMENT_BY_CAMPAIGN = new Map<
  string,
  Map<PlacementId, PlacementDerivedMetrics>
>();
const EMPTY_AUDIENCE_MAP = emptyAudienceMetricsMap();
const DEMO_AUDIENCE_MAP = demoAudienceMetricsMap();

async function fetchCampaignRows(
  profileId: string,
  startDate: string,
  endDate: string
): Promise<ReportRow[]> {
  const res = await adsApi.getSpReportRows(profileId, {
    startDate,
    endDate,
    adProducts: reportAdProductsQueryParam([]),
    limit: WORKSPACE_REPORT_ROW_LIMIT,
  });
  if (!res.success) throw new Error('Failed to load campaign report metrics');
  return parseReportRowsPayload(res.data).rows;
}

async function fetchKeywordRows(
  profileId: string,
  startDate: string,
  endDate: string
): Promise<ReportRow[]> {
  const res = await adsApi.getKeywordReportRows(profileId, {
    startDate,
    endDate,
    adProducts: keywordReportAdProductsQueryParam(),
    limit: WORKSPACE_REPORT_ROW_LIMIT,
  });
  if (!res.success) throw new Error('Failed to load keyword report metrics');
  return parseReportRowsPayload(res.data).rows;
}

async function fetchTargetRows(
  profileId: string,
  startDate: string,
  endDate: string
): Promise<ReportRow[]> {
  const res = await adsApi.getTargetReportRows(profileId, {
    startDate,
    endDate,
    adProducts: targetReportAdProductsQueryParam(),
    limit: WORKSPACE_REPORT_ROW_LIMIT,
  });
  if (!res.success) throw new Error('Failed to load target report metrics');
  return parseReportRowsPayload(res.data).rows;
}

async function fetchAdProductSummary(
  profileId: string,
  rangeState: SnapshotRangeState,
  scopedCampaignLookupKeys: Set<string> | null
) {
  const params: {
    startDate: string;
    endDate: string;
    compareStartDate?: string;
    compareEndDate?: string;
    campaignKeys?: string;
  } = {
    startDate: rangeState.current.startDate,
    endDate: rangeState.current.endDate,
  };
  if (rangeState.compareEnabled) {
    params.compareStartDate = rangeState.previous.startDate;
    params.compareEndDate = rangeState.previous.endDate;
  }
  const campaignKeys = adProductCampaignKeysParam(scopedCampaignLookupKeys);
  if (campaignKeys) params.campaignKeys = campaignKeys;

  const res = await adsApi.getAdProductSummary(profileId, params);
  if (!res.success) throw new Error('Failed to load ad product summary');
  return adProductMapsFromSummary((res.data ?? {}) as AdProductSummaryPayload);
}

async function fetchTargetingTypeSummary(
  profileId: string,
  rangeState: SnapshotRangeState,
  scopedCampaignLookupKeys: Set<string> | null
) {
  const params: {
    startDate: string;
    endDate: string;
    compareStartDate?: string;
    compareEndDate?: string;
    campaignKeys?: string;
  } = {
    startDate: rangeState.current.startDate,
    endDate: rangeState.current.endDate,
  };
  if (rangeState.compareEnabled) {
    params.compareStartDate = rangeState.previous.startDate;
    params.compareEndDate = rangeState.previous.endDate;
  }
  const campaignKeys = campaignKeysQueryParam(scopedCampaignLookupKeys);
  if (campaignKeys) params.campaignKeys = campaignKeys;

  const res = await adsApi.getTargetingTypeSummary(profileId, params);
  if (!res.success) throw new Error('Failed to load targeting type summary');
  return targetingTypeMapsFromSummary((res.data ?? {}) as TargetingTypeSummaryPayload);
}

async function fetchMatchTypeSummary(
  profileId: string,
  rangeState: SnapshotRangeState,
  scopedCampaignLookupKeys: Set<string> | null
) {
  const params: {
    startDate: string;
    endDate: string;
    compareStartDate?: string;
    compareEndDate?: string;
    campaignKeys?: string;
  } = {
    startDate: rangeState.current.startDate,
    endDate: rangeState.current.endDate,
  };
  if (rangeState.compareEnabled) {
    params.compareStartDate = rangeState.previous.startDate;
    params.compareEndDate = rangeState.previous.endDate;
  }
  const campaignKeys = campaignKeysQueryParam(scopedCampaignLookupKeys);
  if (campaignKeys) params.campaignKeys = campaignKeys;

  const res = await adsApi.getMatchTypeSummary(profileId, params);
  if (!res.success) throw new Error('Failed to load match type summary');
  return matchTypeMapsFromSummary((res.data ?? {}) as MatchTypeSummaryPayload);
}

async function fetchPlacementSummary(
  profileId: string,
  rangeState: SnapshotRangeState,
  scopedCampaignLookupKeys: Set<string> | null
) {
  const params: {
    startDate: string;
    endDate: string;
    compareStartDate?: string;
    compareEndDate?: string;
    campaignKeys?: string;
  } = {
    startDate: rangeState.current.startDate,
    endDate: rangeState.current.endDate,
  };
  if (rangeState.compareEnabled) {
    params.compareStartDate = rangeState.previous.startDate;
    params.compareEndDate = rangeState.previous.endDate;
  }
  const campaignKeys = campaignKeysQueryParam(scopedCampaignLookupKeys);
  if (campaignKeys) params.campaignKeys = campaignKeys;

  const res = await adsApi.getPlacementSummary(profileId, params);
  if (!res.success) throw new Error('Failed to load placement summary');
  return placementMapsFromSummary((res.data ?? {}) as PlacementSummaryPayload);
}

async function fetchPlacementRows(
  profileId: string,
  startDate: string,
  endDate: string
): Promise<PlacementReportRow[]> {
  const res = await adsApi.getPlacementReportRows(profileId, { startDate, endDate });
  if (!res.success) throw new Error('Failed to load placement report metrics');
  return parseReportRowsPayload(res.data).rows as PlacementReportRow[];
}

export type WorkspaceCoreReportMetricsResult = {
  current: WorkspaceMetricsMaps;
  previous: WorkspaceMetricsMaps;
  currentByTargetingType: Map<TargetingTypeId, TargetingTypeDerivedMetrics>;
  previousByTargetingType: Map<TargetingTypeId, TargetingTypeDerivedMetrics>;
  targetingTypeTotals: {
    current: TargetingTypeDerivedMetrics;
    previous: TargetingTypeDerivedMetrics;
  };
  currentByMatchType: Map<MatchTypeId, MatchTypeDerivedMetrics>;
  previousByMatchType: Map<MatchTypeId, MatchTypeDerivedMetrics>;
  matchTypeTotals: {
    current: MatchTypeDerivedMetrics;
    previous: MatchTypeDerivedMetrics;
  };
  currentKeywordLookup: Map<string, ReportRow>;
  previousKeywordLookup: Map<string, ReportRow>;
  previousTargetLookup: Map<string, ReportRow>;
  previousTargetRows: ReportRow[];
  /** Unscoped raw keyword/target rows for attribute → campaign resolution. */
  keywordRows: ReportRow[];
  targetRows: ReportRow[];
  /** Ad-product summary (SP/SB/SD table and KPI totals) still loading. */
  adProductLoading: boolean;
  /** Current-range campaign + keyword + target still loading. */
  coreLoading: boolean;
  /** Previous-range core queries still loading (compare only). */
  previousLoading: boolean;
  /** True once current-range core queries have settled (success or error). */
  coreSettled: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
};

/**
 * Core Workspace metrics: campaign / keyword / target rows.
 * Starts as soon as profileId + date range are valid (does not wait for catalog graph).
 * Previous-range fetches are staggered until current-range core settles.
 */
export function useWorkspaceCoreReportMetrics(
  profileId: string,
  rangeState: SnapshotRangeState,
  graph: EntityGraph | null,
  scopedCampaignLookupKeys: Set<string> | null | undefined
): WorkspaceCoreReportMetricsResult {
  const rangesOk = snapshotRangesValid(rangeState);
  const coreEnabled = Boolean(profileId) && rangesOk;
  const scope = scopedCampaignLookupKeys ?? null;

  const currentCampaignQuery = useQuery({
    queryKey: [
      'workspace-campaign-rows',
      profileId,
      rangeState.current.startDate,
      rangeState.current.endDate,
    ],
    queryFn: () =>
      fetchCampaignRows(profileId, rangeState.current.startDate, rangeState.current.endDate),
    enabled: coreEnabled,
  });

  const currentKeywordQuery = useQuery({
    queryKey: [
      'workspace-keyword-rows',
      profileId,
      rangeState.current.startDate,
      rangeState.current.endDate,
    ],
    queryFn: () =>
      fetchKeywordRows(profileId, rangeState.current.startDate, rangeState.current.endDate),
    enabled: coreEnabled,
  });

  const campaignKeywordSettled =
    coreEnabled &&
    (currentCampaignQuery.isFetched || currentCampaignQuery.isError) &&
    (currentKeywordQuery.isFetched || currentKeywordQuery.isError) &&
    !currentCampaignQuery.isLoading &&
    !currentKeywordQuery.isLoading;

  const currentTargetQuery = useQuery({
    queryKey: [
      'workspace-target-rows',
      profileId,
      rangeState.current.startDate,
      rangeState.current.endDate,
    ],
    queryFn: () =>
      fetchTargetRows(profileId, rangeState.current.startDate, rangeState.current.endDate),
    enabled: campaignKeywordSettled,
  });

  const currentQueries = [currentCampaignQuery, currentKeywordQuery, currentTargetQuery];

  const scopeKey = useMemo(() => campaignKeysQueryParam(scope) ?? '', [scope]);
  const adProductScopeKey = useMemo(() => adProductCampaignKeysParam(scope) ?? '', [scope]);

  const adProductSummaryQuery = useQuery({
    queryKey: [
      'workspace-ad-product-summary',
      profileId,
      rangeState.current.startDate,
      rangeState.current.endDate,
      rangeState.compareEnabled ? rangeState.previous.startDate : '',
      rangeState.compareEnabled ? rangeState.previous.endDate : '',
      rangeState.compareEnabled,
      adProductScopeKey,
    ],
    queryFn: () => fetchAdProductSummary(profileId, rangeState, scope),
    enabled: coreEnabled,
  });

  const targetingTypeSummaryQuery = useQuery({
    queryKey: [
      'workspace-targeting-type-summary',
      profileId,
      rangeState.current.startDate,
      rangeState.current.endDate,
      rangeState.compareEnabled ? rangeState.previous.startDate : '',
      rangeState.compareEnabled ? rangeState.previous.endDate : '',
      rangeState.compareEnabled,
      scopeKey,
    ],
    queryFn: () => fetchTargetingTypeSummary(profileId, rangeState, scope),
    enabled: coreEnabled,
  });

  const matchTypeSummaryQuery = useQuery({
    queryKey: [
      'workspace-match-type-summary',
      profileId,
      rangeState.current.startDate,
      rangeState.current.endDate,
      rangeState.compareEnabled ? rangeState.previous.startDate : '',
      rangeState.compareEnabled ? rangeState.previous.endDate : '',
      rangeState.compareEnabled,
      scopeKey,
    ],
    queryFn: () => fetchMatchTypeSummary(profileId, rangeState, scope),
    enabled: coreEnabled,
  });

  const coreSettled =
    coreEnabled &&
    currentQueries.every((q) => q.isFetched || q.isError) &&
    !currentQueries.some((q) => q.isPending);

  const previousEnabled = coreEnabled && rangeState.compareEnabled && coreSettled;

  const previousCampaignQuery = useQuery({
    queryKey: [
      'workspace-campaign-rows',
      profileId,
      rangeState.previous.startDate,
      rangeState.previous.endDate,
    ],
    queryFn: () =>
      fetchCampaignRows(profileId, rangeState.previous.startDate, rangeState.previous.endDate),
    enabled: previousEnabled,
  });

  const previousKeywordQuery = useQuery({
    queryKey: [
      'workspace-keyword-rows',
      profileId,
      rangeState.previous.startDate,
      rangeState.previous.endDate,
    ],
    queryFn: () =>
      fetchKeywordRows(profileId, rangeState.previous.startDate, rangeState.previous.endDate),
    enabled: previousEnabled,
  });

  const previousCampaignKeywordSettled =
    previousEnabled &&
    (previousCampaignQuery.isFetched || previousCampaignQuery.isError) &&
    (previousKeywordQuery.isFetched || previousKeywordQuery.isError) &&
    !previousCampaignQuery.isLoading &&
    !previousKeywordQuery.isLoading;

  const previousTargetQuery = useQuery({
    queryKey: [
      'workspace-target-rows',
      profileId,
      rangeState.previous.startDate,
      rangeState.previous.endDate,
    ],
    queryFn: () =>
      fetchTargetRows(profileId, rangeState.previous.startDate, rangeState.previous.endDate),
    enabled: previousCampaignKeywordSettled,
  });

  const previousQueries = [previousCampaignQuery, previousKeywordQuery, previousTargetQuery];

  const current = useMemo(() => {
    if (!graph) return EMPTY_MAPS;
    return buildWorkspaceMetricsMaps(
      currentCampaignQuery.data ?? [],
      currentKeywordQuery.data ?? [],
      currentTargetQuery.data ?? [],
      graph,
      scope
    );
  }, [
    graph,
    currentCampaignQuery.data,
    currentKeywordQuery.data,
    currentTargetQuery.data,
    scope,
  ]);

  const previous = useMemo(() => {
    if (!graph || !rangeState.compareEnabled) return EMPTY_MAPS;
    return buildWorkspaceMetricsMaps(
      previousCampaignQuery.data ?? [],
      previousKeywordQuery.data ?? [],
      previousTargetQuery.data ?? [],
      graph,
      scope
    );
  }, [
    graph,
    rangeState.compareEnabled,
    previousCampaignQuery.data,
    previousKeywordQuery.data,
    previousTargetQuery.data,
    scope,
  ]);

  const currentByTargetingType =
    targetingTypeSummaryQuery.data?.current ?? EMPTY_TARGETING_TYPE_MAP;
  const previousByTargetingType = rangeState.compareEnabled
    ? (targetingTypeSummaryQuery.data?.previous ?? EMPTY_TARGETING_TYPE_MAP)
    : EMPTY_TARGETING_TYPE_MAP;
  const targetingTypeTotals = {
    current: targetingTypeSummaryQuery.data?.currentTotals ?? EMPTY_TARGETING_TYPE_MAPS.currentTotals,
    previous: rangeState.compareEnabled
      ? (targetingTypeSummaryQuery.data?.previousTotals ?? EMPTY_TARGETING_TYPE_MAPS.previousTotals)
      : EMPTY_TARGETING_TYPE_MAPS.previousTotals,
  };

  const currentByMatchType = matchTypeSummaryQuery.data?.current ?? EMPTY_MATCH_TYPE_MAP;
  const previousByMatchType = rangeState.compareEnabled
    ? (matchTypeSummaryQuery.data?.previous ?? EMPTY_MATCH_TYPE_MAP)
    : EMPTY_MATCH_TYPE_MAP;
  const matchTypeTotals = {
    current: matchTypeSummaryQuery.data?.currentTotals ?? EMPTY_MATCH_TYPE_MAPS.currentTotals,
    previous: rangeState.compareEnabled
      ? (matchTypeSummaryQuery.data?.previousTotals ?? EMPTY_MATCH_TYPE_MAPS.previousTotals)
      : EMPTY_MATCH_TYPE_MAPS.previousTotals,
  };

  const currentKeywordLookup = useMemo(() => {
    const scoped = filterKeywordRowsInScope(currentKeywordQuery.data ?? [], scope);
    return buildKeywordRowLookup(scoped);
  }, [currentKeywordQuery.data, scope]);

  const previousKeywordLookup = useMemo(() => {
    if (!rangeState.compareEnabled) return new Map<string, ReportRow>();
    const scoped = filterKeywordRowsInScope(previousKeywordQuery.data ?? [], scope);
    return buildKeywordRowLookup(scoped);
  }, [previousKeywordQuery.data, scope, rangeState.compareEnabled]);

  const previousTargetLookup = useMemo(() => {
    if (!rangeState.compareEnabled) return new Map<string, ReportRow>();
    const scoped = filterKeywordRowsInScope(previousTargetQuery.data ?? [], scope);
    return buildTargetRowLookup(scoped);
  }, [previousTargetQuery.data, scope, rangeState.compareEnabled]);

  const previousTargetRows = useMemo(() => {
    if (!rangeState.compareEnabled) return EMPTY_REPORT_ROWS;
    return filterKeywordRowsInScope(previousTargetQuery.data ?? [], scope);
  }, [previousTargetQuery.data, scope, rangeState.compareEnabled]);

  const adProductMaps = adProductSummaryQuery.data ?? null;
  const currentWithAdProduct = useMemo(() => {
    return { ...current, byAdProduct: adProductMaps?.current ?? emptyAdProductMap() };
  }, [current, adProductMaps]);
  const previousWithAdProduct = useMemo(() => {
    const byAdProduct =
      rangeState.compareEnabled && adProductMaps
        ? adProductMaps.previous
        : emptyAdProductMap();
    return { ...previous, byAdProduct };
  }, [previous, adProductMaps, rangeState.compareEnabled]);

  const currentPending =
    currentQueries.some((q) => q.isPending) ||
    targetingTypeSummaryQuery.isPending ||
    matchTypeSummaryQuery.isPending;
  const adProductLoading = !coreEnabled || adProductSummaryQuery.isPending;
  // Disabled queries have isLoading=false (idle). isPending stays true until data exists,
  // so staggered target fetches cannot leak an empty "—" table.
  const previousLoading =
    rangeState.compareEnabled &&
    (!coreSettled || previousQueries.some((q) => q.isPending));
  const coreLoading = !coreEnabled || !graph || currentPending;
  const isFetching =
    currentQueries.some((q) => q.isFetching) ||
    previousQueries.some((q) => q.isFetching) ||
    adProductSummaryQuery.isFetching ||
    targetingTypeSummaryQuery.isFetching ||
    matchTypeSummaryQuery.isFetching;
  const firstError =
    currentQueries.find((q) => q.error)?.error ??
    previousQueries.find((q) => q.error)?.error ??
    adProductSummaryQuery.error ??
    targetingTypeSummaryQuery.error ??
    matchTypeSummaryQuery.error;

  const refetch = () => {
    for (const q of currentQueries) void q.refetch();
    for (const q of previousQueries) void q.refetch();
    void adProductSummaryQuery.refetch();
    void targetingTypeSummaryQuery.refetch();
    void matchTypeSummaryQuery.refetch();
  };

  return {
    current: currentWithAdProduct,
    previous: previousWithAdProduct,
    currentByTargetingType,
    previousByTargetingType,
    targetingTypeTotals,
    currentByMatchType,
    previousByMatchType,
    matchTypeTotals,
    currentKeywordLookup,
    previousKeywordLookup,
    previousTargetLookup,
    previousTargetRows,
    /** Unscoped raw rows for attribute → campaign resolution. */
    keywordRows: currentKeywordQuery.data ?? EMPTY_REPORT_ROWS,
    targetRows: currentTargetQuery.data ?? EMPTY_REPORT_ROWS,
    adProductLoading,
    coreLoading,
    previousLoading,
    coreSettled: Boolean(coreSettled),
    isFetching,
    error: firstError instanceof Error ? firstError : firstError ? new Error(String(firstError)) : null,
    refetch,
  };
}

export type WorkspaceDeferredReportMetricsResult = {
  currentByPlacement: Map<PlacementId, PlacementDerivedMetrics>;
  previousByPlacement: Map<PlacementId, PlacementDerivedMetrics>;
  /** Per-campaign placement metrics keyed by campaignLookupKey (unscoped). */
  placementMetricsByCampaign: Map<string, Map<PlacementId, PlacementDerivedMetrics>>;
  /** Previous-range per-campaign placement metrics; empty when compare is off. */
  previousPlacementMetricsByCampaign: Map<string, Map<PlacementId, PlacementDerivedMetrics>>;
  currentByAudience: Map<AudienceSegmentId, AudienceDerivedMetrics>;
  previousByAudience: Map<AudienceSegmentId, AudienceDerivedMetrics>;
  deferredLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
};

/**
 * Lower Workspace sections: placement (+ audience placeholder).
 * Search terms are fetched via useWorkspaceSearchTermsPage.
 * Enabled only after core current-range metrics have settled.
 */
export function useWorkspaceDeferredReportMetrics(
  profileId: string,
  rangeState: SnapshotRangeState,
  _graph: EntityGraph | null,
  scopedCampaignLookupKeys: Set<string> | null | undefined,
  coreSettled: boolean,
  sectionsVisible = true
): WorkspaceDeferredReportMetricsResult {
  const rangesOk = snapshotRangesValid(rangeState);
  const deferredEnabled =
    Boolean(profileId) && rangesOk && coreSettled && sectionsVisible;
  const scope = scopedCampaignLookupKeys ?? null;

  const currentPlacementQuery = useQuery({
    queryKey: [
      'workspace-placement-rows',
      profileId,
      rangeState.current.startDate,
      rangeState.current.endDate,
    ],
    queryFn: () =>
      fetchPlacementRows(profileId, rangeState.current.startDate, rangeState.current.endDate),
    enabled: deferredEnabled,
  });

  const previousPlacementQuery = useQuery({
    queryKey: [
      'workspace-placement-rows',
      profileId,
      rangeState.previous.startDate,
      rangeState.previous.endDate,
    ],
    queryFn: () =>
      fetchPlacementRows(profileId, rangeState.previous.startDate, rangeState.previous.endDate),
    enabled: deferredEnabled && rangeState.compareEnabled,
  });

  const scopeKey = campaignKeysQueryParam(scope) ?? '';
  const placementSummaryQuery = useQuery({
    queryKey: [
      'workspace-placement-summary',
      profileId,
      rangeState.current.startDate,
      rangeState.current.endDate,
      rangeState.compareEnabled ? rangeState.previous.startDate : '',
      rangeState.compareEnabled ? rangeState.previous.endDate : '',
      rangeState.compareEnabled,
      scopeKey,
    ],
    queryFn: () => fetchPlacementSummary(profileId, rangeState, scope),
    enabled: deferredEnabled,
  });

  const currentByPlacement = placementSummaryQuery.data?.current ?? EMPTY_PLACEMENT_MAP;
  const previousByPlacement = rangeState.compareEnabled
    ? (placementSummaryQuery.data?.previous ?? EMPTY_PLACEMENT_MAP)
    : EMPTY_PLACEMENT_MAP;

  const placementMetricsByCampaign = useMemo(() => {
    return aggregatePlacementMetricsByCampaign(currentPlacementQuery.data ?? []);
  }, [currentPlacementQuery.data]);

  const previousPlacementMetricsByCampaign = useMemo(() => {
    if (!rangeState.compareEnabled) return EMPTY_PLACEMENT_BY_CAMPAIGN;
    return aggregatePlacementMetricsByCampaign(previousPlacementQuery.data ?? []);
  }, [rangeState.compareEnabled, previousPlacementQuery.data]);

  const deferredQueries = [currentPlacementQuery, previousPlacementQuery, placementSummaryQuery];

  const deferredLoading =
    !coreSettled ||
    !deferredEnabled ||
    currentPlacementQuery.isPending ||
    placementSummaryQuery.isPending ||
    (rangeState.compareEnabled && previousPlacementQuery.isLoading);

  const isFetching = deferredQueries.some((q) => q.isFetching);
  const firstError = deferredQueries.find((q) => q.error)?.error;

  const refetch = () => {
    void currentPlacementQuery.refetch();
    void previousPlacementQuery.refetch();
    void placementSummaryQuery.refetch();
  };

  const audienceMap =
    profileId === DEMO_PROFILE_ID ? DEMO_AUDIENCE_MAP : EMPTY_AUDIENCE_MAP;

  return {
    currentByPlacement,
    previousByPlacement,
    placementMetricsByCampaign,
    previousPlacementMetricsByCampaign,
    currentByAudience: audienceMap,
    previousByAudience: audienceMap,
    deferredLoading,
    isFetching,
    error: firstError instanceof Error ? firstError : firstError ? new Error(String(firstError)) : null,
    refetch,
  };
}

/** @deprecated Prefer core + deferred hooks; kept for any external imports. */
export function useWorkspaceReportMetrics(
  profileId: string,
  rangeState: SnapshotRangeState,
  graph: EntityGraph | null,
  scopedCampaignLookupKeys: Set<string> | null | undefined
) {
  const core = useWorkspaceCoreReportMetrics(
    profileId,
    rangeState,
    graph,
    scopedCampaignLookupKeys
  );
  const deferred = useWorkspaceDeferredReportMetrics(
    profileId,
    rangeState,
    graph,
    scopedCampaignLookupKeys,
    core.coreSettled
  );

  return {
    ...core,
    ...deferred,
    isLoading: core.coreLoading || deferred.deferredLoading,
    isFetching: core.isFetching || deferred.isFetching,
    error: core.error ?? deferred.error,
    refetch: () => {
      core.refetch();
      deferred.refetch();
    },
  };
}

export { metricsForKeywordGroup };
